/* Core & libraries */
import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "react-jss";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { ErrorMessage, Field, Formik, useFormik } from "formik";
import { Row, Col, InputGroup, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router";
import Skeleton from "react-loading-skeleton";

/* Helpers, constants, custom methods, internal libraries */
import {
  createValidationPolicyYupSchema,
  useValidationService
} from "../../../../resources/validations/validations";
import { useCompaniesService } from "../../../../services/companiesService";
import { useGeneralService } from "../../../../services/generalService";
import { useUserContext } from "../../../../contexts/userContext";
import { useAnalytics } from "../../../../hooks/useAnalytics";
import { useHasPermission } from "../../../../hooks/useHasPermission";
import {
  AIRLINES,
  DEDUPE_HA_ERROR_CODE,
  IS_ADMIN_PERMISSION,
  PAGE_TITLES,
  getNorthAmericaCountries
} from '../../../../constants/appConstants';
import { HOME_ROUTE } from "../../../../constants/routes.js";
import { useLocaleContext } from "../../../../contexts/localeContext";
import eventEmitter from '../../../../services/eventEmitterService';
import { moveByPriority } from "../../../../helpers/general";
import { useFareAccessService } from "../../../../services/fareAccessService";

/* Components */
import UCButton from "../../../buttons";
import DeleteCompanyMasterModal from "../../../modals/deleteCompanyMasterModal";
import UCSelect from "../../../ucselect";
import UCModal from "../../../ucmodal";
import GeneralTabSkeleton from "../../skeletonGeneralTab";
import { ReactComponent as EditEmailSvg } from '../../../../resources/images/edit.svg';
import { ReactComponent as DeleteCompanyIcon } from "../../../../resources/images/delete-company-icon.svg";
import { ReactComponent as WarningCompanyDeleteIcon } from "../../../../resources/images/warning-company-delete-icon.svg";
import { ReactComponent as InfoIconSvg } from "../../../../resources/images/info-icon.svg";
import UpdateCitiEmailModal from "../../../admin/companyDetailsTabbedNav/updateCitiEmailModal";
import UCPhoneCountrySelector from "../../../ucPhoneCountrySelector";
import ASTextLoadingSpinner from "../../../spinners/asTextLoadingSpinner";

/* Styles */
import styles, { countrySelect, hawaianSelect, selectError } from "./styles.js"
import { colors } from "../../../../constants/colors";
import generalStyles from "../../../../resources/styles/general";
import classnames from 'classnames';

const messages = defineMessages({
  statusPlaceholderMessage: {
    id: 'settings.statusPlaceholder'
  },
  namePlaceholderMessage: {
    id: 'settings.namePlaceholder'
  },
  countryPlaceholderMessage: {
    id: 'settings.countryPlaceholder'
  },
  address1PlaceholderMessage: {
    id: 'settings.address1Placeholder'
  },
  address2PlaceholderMessage: {
    id: 'settings.address2Placeholder'
  },
  cityPlaceholderMessage: {
    id: 'settings.cityPlaceholder'
  },
  statePlaceholderMessage: {
    id: 'settings.statePlaceholder'
  },
  postalCodePlaceholderMessage: {
    id: 'settings.postalCodePlaceholder'
  },
  removePlaceholder: {
    id: 'settings.removePlaceholder'
  },
  accountIdPlaceholderMessage: {
    id: 'settings.accountIdPlaceholder'
  },
  discountCodePlaceholderMessage: {
    id: 'settings.discountCodePlaceholder'
  },
  businessWebsitePlaceholderMessage: {
    id: 'settings.businessWebsitePlaceholder'
  },
  referralCodePlaceholderMessage: {
    id: 'settings.referralCodePlaceholder'
  },
  taxIdPlaceholderMessage: {
    id: 'settings.taxIdPlaceholder'
  },
  businessAccNrPlaceholderMessage: {
    id: 'settings.businessAccNrPlaceholder'
  },
  businessNrPlaceholderMessage: {
    id: 'settings.businessNrPlaceholder'
  },
  mileagePlanNumberPlaceholderMessage: {
    id: 'admin.editCompany.mileagePlanNumberPlaceholder'
  },
  arcIataPlaceholderMessage: {
    id: 'admin.editCompany.travelAgencyPlaceholder'
  },
  ticketPreferencePlaceholderMessage: {
    id: "admin.editCompany.ticketPreferencePlaceholder",
  },
  companyStarPlaceholder: {
    id: "admin.editCompany.companyStar.placeholder"
  },
  haCorporateProgramEnrolledPlaceholderMessage: {
    id: "admin.editCompany.haCorporateProgramEnrolledPlaceholder",
  },
  haCorporateProgramEnrolledYesMessage: {
    id: "admin.editCompany.haCorporateProgramEnrolledYes",
  },
  haCorporateProgramEnrolledNoMessage: {
    id: "admin.editCompany.haCorporateProgramEnrolledNo",
  },
  hawaiianNumberPlaceholderMessage: {
    id: "admin.editCompany.hawaiianNumberPlaceholder"
  },
  hawaiianNumberDedupe: {
    id: "companyAdditionalDetailsValidation.hawaiianNumberDedupe"
  }
})


const GeneralTabAS = (props) => {

  const theme = useTheme();
  const classes = styles({ theme });
  const general = generalStyles({ theme });

  const formRef = useRef(null);
  const intl = useIntl();
  const northAmericaCountries = getNorthAmericaCountries(intl);
  const globalLocale = useLocaleContext();
  const { updateCompany, deleteCompany, getCompanyAdmins, getCompanyDetails, setOverrideThreshold } = useCompaniesService();
  const { getStates, getCountries } = useGeneralService();
  const { getDiscountTermsContent } = useFareAccessService();

  const navigate = useNavigate();
  const [companyDetails, setCompanyDetails] = useState(null);

  const { companyInfoMockedApiValidationObjAS } = useValidationService()
  const [testSchema, setTestSchema] = useState({});

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [states, setStates] = useState([]);
  const [companyAdmins, setCompanyAdmins] = useState([])
  const [showUpdateCitiEmailModal, setShowUpdateCitiEmailModal] = useState(false);
  const [fullName, setFullName] = useState("")
  const [countriesData, setCountriesData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [discountTermsContent, setDiscountTermsContent] = useState(null);
  const [isLoadingDiscountTerms, setIsLoadingDiscountTerms] = useState(false);

  const userDetails = useUserContext();
  const { initAnalytics } = useAnalytics();
  const { checkPermission } = useHasPermission();

  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId;
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  const ticketPreferenceOptions = [
    {
      value: "NO_LIMIT",
      label: (
        <FormattedMessage id="admin.editCompany.ticketPreferenceOptions.noLimitations" />
      ),
    },
    {
      value: "DISABLE",
      label: (
        <FormattedMessage id="admin.editCompany.ticketPreferenceOptions.disableWallet" />
      ),
    },
  ];

  const guestProfileVisibilityOptions = [
    {
      value: "8",
      label: (
        <FormattedMessage id="admin.editCompany.guestVisibilityOptions.admin" />
      ),
    },
    {
      value: "8,7",
      label: (
        <FormattedMessage id="admin.editCompany.guestVisibilityOptions.adminTraveler" />
      ),
    },
  ]

  useEffect(() => {
    const initUtagData = {
      channel: 'content',
      pageName: PAGE_TITLES["settings"]
    };
    initAnalytics(initUtagData, 'init call', 'view')

    //TODO: api call to get validations //simpleMockedApiValidationObj
    const result = createValidationPolicyYupSchema(companyInfoMockedApiValidationObjAS);
    setTestSchema(result)
  }, [])

  useEffect(() => {
    (async () => {
      const response = await getCompanyDetails(companyEntityId);
      if (response?.address?.country) {
        handleCountryChange(response.address.country);
      } else {
        handleCountryChange("US")
      }
      setCompanyDetails({
        ...response,
        companyId: companyEntityId,
      })
      const responseAdmins = await getCompanyAdmins(companyEntityId);
      setCompanyAdmins(responseAdmins?.usersByRoleDTOList)
      const countriesResponse = await getCountries();
      setCountriesData(moveByPriority(countriesResponse, "value") || []);
    })();
  }, [])

  useEffect(() => {
    const name = companyAdmins?.find(item => item.userEntityId === companyDetails?.primaryAdminId)
    const fullName = name?.firstName + " " + name?.lastName
    setFullName(fullName)
  }, [companyAdmins]);

  useEffect(() => {
    const handleCompanyDeletionPending = async () => {
      const response = await getCompanyDetails(companyEntityId);
      setCompanyDetails({
        ...response,
        companyId: companyEntityId,
      })
    };

    eventEmitter.on('companyDeletionPending', handleCompanyDeletionPending);

    return () => {
      eventEmitter.off('companyDeletionPending', handleCompanyDeletionPending);
    };
  }, [companyEntityId, getCompanyDetails]);

  useEffect(() => {
    if (showModal) {
      (async () => {
        setIsLoadingDiscountTerms(true);
        const content = await getDiscountTermsContent();
        setDiscountTermsContent(content);
        setIsLoadingDiscountTerms(false);
      })();
    }
  }, [showModal]);

  const taxFieldValidation = (values) => {
    let error;
    const taxIdRegex = /^[0-9]*$/;
    const buissnessNumberRegex = /^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\-, ']+)*$/;

    const taxId = values.taxId.replaceAll("-", "")
    if (values.countryCode === 'CA') {
      if (!buissnessNumberRegex.test(taxId)) {
        error = <FormattedMessage id='taxIdValidation.taxMatches' />
      }
    } else if (values.countryCode === 'US') {
      if (!taxIdRegex.test(taxId)) {
        error = <FormattedMessage id='taxIdValidation.taxMatches' />;
      }
    }
    return error
  }

  const handleTaxIdBlur = (values, setFieldValue, validateField, setFieldTouched) => {
    setFieldValue("taxId", values.taxId.replaceAll("-", ""), false)
    validateField("taxId")
    setFieldTouched("taxId")
  }

  const submitForm = async (values, setSubmitting, setFieldError) => {
    const payload = {
      companyId: companyDetails.companyId,
      primaryAdminId: values.primaryContact,
      companyName: values.companyName,
      taxId: values.taxId,
      phoneCountryCode: values.phoneCountryCode,
      phoneNumber: values.phoneNumber,
      address: {
        country: values.countryCode,
        streetAddress: values.streetAddress1,
        streetAddress2: values.streetAddress2,
        city: values.city,
        state: values.state,
        postalCode: values.postalCode,
      },
      previousAccountNumber: values.previousAccountNumber,
      discountCode: values.discountCode,
      businessWebsite: values.businessWebsite,
      referralCode: values.referralCode,
      cargo: values.cargo,
      ticketCreditPreference: values.ticketCreditPreference,
      guestProfileVisibility: values.guestProfileVisibility,
      companyStar: values.companyStar,
      hawaiianNumber: values.haCorporateProgramEnrolled ? values.hawaiianNumber : ''
    };

    if (checkPermission("F_VIEW_COMPANY_TAXID")) {
      payload.taxId = values.taxId;
    }
    if (checkPermission("F_MANAGE_COMPANY_MEMBERSHIP_NUMBER")) {
      payload.companyMembershipNumber = values.businessAccountNumber;
    }
    if (checkPermission("F_MANAGE_COMPANY_IATA")) {
      payload.travelAgencyNumber = values.travelAgencyNumber;
    }
    if (checkPermission("F_MANAGE_COMPANY_TAXID")) {
      payload.taxId = values.taxId;
    }
    const response = await updateCompany(payload);
    if (Array.isArray(response.errors) && response.errors.find(error => error.errorCode === DEDUPE_HA_ERROR_CODE)) {
      setFieldError('hawaiianNumber', intl.formatMessage(messages.hawaiianNumberDedupe));
    }

    setSubmitting(false);
  }

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit()
    }
  }

  const cancelUpdate = () => {
    if (formRef.current) {
      formRef.current.resetForm()
    }
    navigate(HOME_ROUTE);
    window.scrollTo(0, 0);
  }

  const handleCountryChange = async (item) => {
    const response = await getStates(item);
    if (response) {
      setStates(response.states)
    }
  }

  let countries;
  switch (providerCode) {
    case AIRLINES.AA:
      countries = [
        ...northAmericaCountries
      ];
      break;
    default:
      countries = countriesData;
      break;
  }

  const fetchCompanyDetails = async () => {
    const response = await getCompanyDetails(companyEntityId);
    setCompanyDetails({
      ...response,
      companyId: companyEntityId,
    });
  };

  const maskedTaxId = (taxNumber) => {
    if (!taxNumber || isNaN(taxNumber)) {
      return taxNumber
    } else {
      const numberStr = taxNumber?.toString();
      const lastFourDigits = numberStr?.slice(-4);
      const maskedPart = '*'.repeat(numberStr?.length - 4);
      const maskedNumber = `${maskedPart}${lastFourDigits}`;

      return (maskedNumber)
    }
  };

  const hawaiianNumberDisabled = !checkPermission("F_PROVIDER_ADMIN");

  return (
    <>
      <div className={classes.details}>
        <div>
          <div className={classes.title}>
            <FormattedMessage id="settings.titleSection" />
          </div>
          <div className={classes.description}>
            <FormattedMessage id="settings.description" />
          </div>
        </div>
        <div className={classes.btnContainer}>
          {companyDetails && companyAdmins?.length > 0 && states.length > 0 ?
            <>
              <UCButton
                type="default"
                className={classes.buttonCancel}
                onClick={cancelUpdate}
                text={<FormattedMessage id="settings.cancel" />}
              />
              <UCButton
                type="primary"
                className={classes.button}
                onClick={handleFormSubmit}
                text={<FormattedMessage id="settings.save" />}
                spinner={{
                  name: "updateCompanySpinner",
                  size: 20,
                  color: colors.white,
                }}
              />
            </>
            :
            <div className={classes.secondRowAuto}>
              <Skeleton count={1} width={68} className={classes.longSkM} />
              <Skeleton count={1} width={68} className={classes.longSk} />
            </div>
          }
        </div>
      </div>

      <div className={classes.dividerContainer}>
        <hr className={classes.border} />
      </div>
      {companyDetails && companyAdmins?.length > 0 && states.length > 0 ? (
        <div className={classes.editCompany}>
          {
            companyDetails &&
            <Formik
              innerRef={formRef}
              enableReinitialize={true}
              initialValues={{
                primaryContact: companyDetails?.primaryAdminId,
                companyName: companyDetails?.companyName,
                countryCode: companyDetails?.address?.country,
                streetAddress1: companyDetails?.address?.streetAddress,
                streetAddress2: companyDetails?.address?.streetAddress2 ? companyDetails?.address?.streetAddress2 : "",
                city: companyDetails?.address?.city,
                state: companyDetails?.address?.state,
                postalCode: companyDetails?.address?.postalCode,
                businessWebsite: companyDetails?.businessWebsite || "",
                phoneCountryCode: companyDetails?.phoneCountryCode ? companyDetails?.phoneCountryCode : "US",
                phoneNumber: companyDetails?.phoneNumber?.trim() || "",
                taxId: companyDetails?.taxId ? companyDetails?.taxId : "",
                discountCode: companyDetails?.discountCode,
                referralCode: companyDetails?.referralCode || "",
                previousAccountNumber: companyDetails?.previousAccountNumber ? companyDetails?.previousAccountNumber : "",
                businessAccountNumber: companyDetails?.companyMembershipNumber,
                cargo: companyDetails?.cargo ? companyDetails?.cargo : false,
                fullName: fullName ? fullName : "",
                companyStar: companyDetails?.companyStar || "",
                ticketCreditPreference: companyDetails?.ticketCreditPreference || "",
                guestProfileVisibility: companyDetails?.guestProfileVisibility || "",
                travelAgencyNumber: companyDetails?.travelAgencyNumber || "",
                haCorporateProgramEnrolled: companyDetails?.hawaiianNumber ? true : false,
                hawaiianNumber: companyDetails?.hawaiianNumber || "",
              }}
              validationSchema={testSchema}
              onSubmit={(values, { setSubmitting, setFieldError }) => {
                submitForm(values, setSubmitting, setFieldError);
              }}
            >
              {({
                values,
                errors,
                touched,
                setFieldTouched,
                handleChange,
                handleBlur,
                handleSubmit,
                resetForm,
                isSubmitting,
                setFieldValue,
                validateField,
                enableReinitialize
              }) => (
                <form onSubmit={handleSubmit}>
                  <div className={classes.formCont}>
                    <div className="row">
                      <div className="col-12 col-md-4">
                        <div className={classes.location}>
                          <FormattedMessage id="settings.contact" />
                        </div>
                        <div className={classes.description}>
                          <FormattedMessage id="settings.contactDescription" />
                        </div>
                      </div>
                      {companyAdmins?.length > 0 && !companyDetails?.mustChangeEmailAddress && (
                        <div className="col-12 col-md-8">
                          <div className={classes.formContainer}>
                            <Field
                              placeholder={"primaryContact"}
                              type="select"
                              name="primaryContact"
                              id="primaryContact"
                              render={({ form }) => (
                                <div>
                                  <label htmlFor="primaryContact" className={classes.label}>
                                    <FormattedMessage id="settings.primaryAdmin" />
                                  </label>
                                  <UCSelect
                                    className={classes.roles}
                                    displayName={<FormattedMessage id="settings.primaryAdmin" />}
                                    options={companyAdmins}
                                    defaultValue={companyAdmins?.find(item => item.userEntityId === values.primaryContact)}
                                    onChange={(item) => form.setFieldValue("primaryContact", item.userEntityId)}
                                    getOptionLabel={(option) => {
                                      return (
                                        <div className={classes.adminLabel}>
                                          <div className={classes.adminName}>{option.firstName} {option.lastName}</div>
                                        </div>
                                      )
                                    }}
                                    getOptionValue={(option) => option.userEntityId}
                                    disabled={!companyDetails?.canUpdatePrimaryAdmin}
                                  />
                                  <ErrorMessage
                                    name="primaryContact"
                                    component="span"
                                    className={classes.error}
                                  />
                                  <p className={classes.pContactSubtext}>
                                    <FormattedMessage id="settings.primaryContact.subText" />
                                  </p>
                                </div>
                              )}
                            />
                          </div>
                        </div>
                      )}
                      {companyDetails?.mustChangeEmailAddress
                        && (
                          <div className="col-12 col-md-8">
                            <div className={classes.formContainer}>
                              <div className={classes.inputMailContainer}>
                                <label htmlFor="status" className={classes.label}>
                                  <FormattedMessage id="admin.editCompany.primaryAdmin" />
                                </label>
                              </div>
                              <div className={classes.editDiv}>
                                <Field
                                  placeholder={intl.formatMessage(messages.namePlaceholderMessage)}
                                  type="text"
                                  name="travelManager"
                                  id="travelManager"
                                  value={fullName}
                                  className={
                                    errors.travelManager && touched.travelManager
                                      ? "input-error"
                                      : null
                                  }
                                  disabled={true}
                                />
                                <UCButton
                                  onClick={() => setShowUpdateCitiEmailModal(true)}
                                  className={classes.editButton}
                                  iconEnd={<EditEmailSvg />}
                                />
                              </div>
                              <ErrorMessage
                                name="travelManeger"
                                component="span"
                                className={classes.error}
                              />
                              <span className={classes.taxIdNote}>
                                <FormattedMessage id={"admin.updateCitiEmailText"} />
                              </span>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>

                  <div className={classes.dividerContainer}>
                    <hr className={classes.border} />
                  </div>

                  <div className={classes.formCont}>
                    <div className="row">
                      <div className="col-12 col-md-4">
                        <div className={classes.location}>
                          <FormattedMessage id="settings.location" />
                        </div>
                        <div className={classes.description}>
                          <FormattedMessage id="settings.update" />
                        </div>
                      </div>
                      <div className="col-12 col-md-8">
                        <div className={classes.formContainer}>
                          <div className={classes.inputContainer}>
                            <label htmlFor="companyName" className={classes.label}>
                              <FormattedMessage id="settings.companyName" />
                              <span aria-hidden="true">*</span>
                            </label>
                            <Field
                              placeholder={intl.formatMessage(messages.namePlaceholderMessage)}
                              type="text"
                              name="companyName"
                              id="companyName"
                              className={
                                errors.companyName && touched.companyName
                                  ? "input-error"
                                  : null
                              }
                              disabled={!checkPermission("F_COMPANYSETTINGS_EDITNAME")}
                            />
                            <ErrorMessage
                              name="companyName"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                          <div className={classes.inputContainer}>
                            <label htmlFor="country" className={classes.label}>
                              <FormattedMessage id="settings.country" />
                              <span aria-hidden="true">*</span>
                            </label>
                            <Field
                              type="select"
                              name="countryCode"
                              id="countryCode"
                              render={({ form }) => (
                                <div>
                                  <UCSelect
                                    isSearchable={true}
                                    className={classes.roles}
                                    styles={countrySelect}
                                    displayName={<FormattedMessage id="settings.countryPlaceholder" />}
                                    options={countries}
                                    defaultValue={countries.find(item => item.value === values.countryCode)}
                                    value={values.countryCode}
                                    getOptionLabel={(option) => option.label}
                                    onBlur={() => setFieldTouched("countryCode", true)}
                                    onChange={(item) => {
                                      form.setFieldValue("countryCode", item.value)
                                      handleCountryChange(item.value)
                                      {
                                        item.value !==  values.countryCode ? form.setFieldValue("state", " ") : null
                                      }                                    }}
                                  />
                                  <ErrorMessage
                                    name="countryCode"
                                    component="span"
                                    className={classes.error}
                                  />
                                </div>
                              )}
                              className={
                                errors.countryCode && touched.countryCode
                                  ? "input-error"
                                  : null
                              }
                            />
                          </div>
                          <div className={classes.inputContainer}>
                            <label htmlFor='taxId' className={classes.label}>
                              {values.countryCode === 'CA' ? (
                                <FormattedMessage id='settings.businessNr' />
                              ) : (
                                <FormattedMessage id='settings.taxId' />
                              )}
                            </label>
                            <Field
                              placeholder={
                                values.countryCode === 'CA'
                                  ? intl.formatMessage(messages.businessNrPlaceholderMessage)
                                  : intl.formatMessage(messages.taxIdPlaceholderMessage)
                              }
                              onBlur={() => handleTaxIdBlur(values, setFieldValue, validateField, setFieldTouched)}
                              type="text"
                              name="taxId"
                              id="taxId"
                              value={!checkPermission(IS_ADMIN_PERMISSION) && providerCode === AIRLINES.AS && companyDetails?.taxId ? maskedTaxId(values?.taxId) : values.taxId}
                              validate={() => !companyDetails?.taxId ? taxFieldValidation(values) : null}
                              className={
                                errors.taxId && touched.taxId
                                  ? "input-error"
                                  : null
                              }
                              disabled={!checkPermission("F_MANAGE_COMPANY_TAXID") || companyDetails?.companyStatus === "REMOVED"}
                            />
                            {
                              !companyDetails?.taxId && providerCode === AIRLINES.AA &&
                              <span className={classes.taxIdNote}>
                                <FormattedMessage id={"taxIdValidation.taxIDNote"} />
                              </span>
                            }
                            <ErrorMessage
                              name="taxId"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                          <div className={classes.inputContainer}>
                            <label htmlFor="travelAgencyNumber" className={classes.label}>
                              <FormattedMessage id="admin.editCompany.travelAgency" />
                            </label>
                            <Field
                              placeholder={intl.formatMessage(messages.arcIataPlaceholderMessage)}
                              type="text"
                              name="travelAgencyNumber"
                              id="travelAgencyNumber"
                              className={
                                errors.travelAgencyNumber && touched.travelAgencyNumber
                                  ? "input-error"
                                  : null
                              }
                              disabled={!checkPermission("F_MANAGE_COMPANY_IATA") || companyDetails?.companyStatus === "REMOVED"}
                            />
                            <ErrorMessage
                              name="travelAgencyNumber"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                          <div className={classes.phoneInputContainer}>
                            <label htmlFor="phoneNumber" className={classes.label}>
                              <FormattedMessage id="settings.phoneNumber" />
                              <span aria-hidden="true">*</span>
                            </label>
                            <UCPhoneCountrySelector
                              providerCode={providerCode}
                              errors={errors}
                              touched={touched}
                              values={values}
                            />
                            <ErrorMessage
                              name="phoneNumber"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                          <div className={classes.inputContainer}>
                            <label htmlFor="streetAddress1" className={classes.label}>
                              <FormattedMessage id="settings.address1" />
                              <span aria-hidden="true">*</span>
                            </label>
                            <Field
                              placeholder={intl.formatMessage(messages.address1PlaceholderMessage)}
                              type="text"
                              name="streetAddress1"
                              id="streetAddress1"
                              className={
                                errors.streetAddress1
                                  && touched.streetAddress1
                                  ? "input-error"
                                  : null
                              }
                            />
                            <ErrorMessage
                              name="streetAddress1"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                          <div className={classes.inputContainer}>
                            <label htmlFor="streetAddress2" className={classes.label}>
                              <FormattedMessage id="settings.address2" />
                            </label>
                            <Field
                              placeholder={intl.formatMessage(messages.address2PlaceholderMessage)}
                              type="text"
                              name="streetAddress2"
                              id="streetAddress2"
                              className={
                                errors.streetAddress2 && touched.streetAddress2
                                  ? 'input-error'
                                  : null
                              }
                            />
                            <ErrorMessage
                              name="streetAddress2"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                          <div className={classes.inputContainer}>
                            <label htmlFor="city" className={classes.label}>
                              <FormattedMessage id="settings.city" />
                            </label>
                            <Field
                              placeholder={intl.formatMessage(messages.cityPlaceholderMessage)}
                              type="text"
                              name="city"
                              id="city"
                              className={
                                errors.city && touched.city
                                  ? 'input-error'
                                  : null
                              }
                            />
                            <ErrorMessage
                              name="city"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                          {
                            states &&
                            <div className={classes.inputContainer}>
                              <label htmlFor="state" className={classes.label}>
                                <FormattedMessage id="settings.state" />
                              </label>
                              <Field
                                type="select"
                                name="state"
                                id="state"
                                render={({ form }) => (
                                  <div>
                                    <UCSelect
                                      className={classes.roles}
                                      styles={ errors.state && touched.state ? selectError : "" }
                                      displayName={<FormattedMessage id="settings.statePlaceholder" />}
                                      value={values.state}
                                      options={states}
                                      onBlur={() => setFieldTouched("state", true)}
                                      defaultValue={states.find(item => item.value === values.state)}
                                      onChange={(item) => form.setFieldValue("state", item.value)}
                                      isSearchable={true}
                                    />
                                    <ErrorMessage
                                      name="state"
                                      component="span"
                                      className={classes.error}
                                    />
                                  </div>
                                )}
                                className={
                                  errors.state && touched.state
                                    ? "input-error"
                                    : null
                                }
                              />
                            </div>
                          }
                          <div className={`${classes.inputContainer} ${classes.lastInputContainer}`}>
                            <label htmlFor="postalCode" className={classes.label}>
                              <FormattedMessage id="settings.postalCode" />
                              <span aria-hidden="true">*</span>
                            </label>
                            <Field
                              placeholder={intl.formatMessage(messages.postalCodePlaceholderMessage)}
                              type="postalCode"
                              name="postalCode"
                              id="postalCode"
                              className={
                                errors.postalCode && touched.postalCode
                                  ? "input-error"
                                  : null
                              }
                            />
                            <ErrorMessage
                              name="postalCode"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={classes.dividerContainer}>
                      <hr className={classes.border} />
                    </div>

                    <div className={classes.formCont}>
                      <div className="row">
                        <div className="col-12 col-md-4">
                          <div className={classes.location}>
                            <FormattedMessage id="admin.editCompany.preference" />
                          </div>
                          <div className={classes.description}>
                            <FormattedMessage id="admin.editCompany.preferenceDescription" />
                          </div>
                        </div>
                        <div className="col-12 col-md-8">
                          <div className={classes.formContainer}>
                            <div className={classes.inputContainer}>
                              <label htmlFor="ticketCreditPreference" className={classes.preferenceLabel}>
                                <FormattedMessage id="admin.editCompany.ticketPreference" />
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip
                                      className={`${general.main} ${classes.tooltip}`}
                                    >
                                      <div className={classes.infoDescription}>
                                        <FormattedMessage
                                          id="admin.editCompany.ticketPreferenceTooltip.info1"
                                          values={{
                                            ul: (item) => <ul>{item}</ul>,
                                            li: (item) => <li>{item}</li>,
                                          }}
                                        />
                                      </div>
                                      <div className={classes.infoDescription}>
                                        <FormattedMessage
                                          id="admin.editCompany.ticketPreferenceTooltip.info2"
                                          values={{
                                            ul: (item) => <ul>{item}</ul>,
                                            li: (item) => <li>{item}</li>,
                                          }}
                                        />
                                      </div>
                                    </Tooltip>
                                  }
                                >
                                  <InfoIconSvg className={classes.infoIcon} />
                                </OverlayTrigger>
                              </label>
                              <Field
                                placeholder={intl.formatMessage(
                                  messages.ticketPreferencePlaceholderMessage
                                )}
                                type="select"
                                name="ticketCreditPreference"
                                id="ticketCreditPreference"
                                render={({ form }) => (
                                  <>
                                    <UCSelect
                                      isSearchable={false}
                                      fieldHasError={
                                        errors.ticketCreditPreference &&
                                        touched.ticketCreditPreference
                                      }
                                      displayName={
                                        <FormattedMessage id="admin.editCompany.ticketPreferencePlaceholder" />
                                      }
                                      value={values.ticketCreditPreference}
                                      options={ticketPreferenceOptions}
                                      defaultValue={ticketPreferenceOptions.find(item => item.value === values.ticketCreditPreference)}
                                      onChange={(item) =>
                                        form.setFieldValue(
                                          "ticketCreditPreference",
                                          item.value
                                        )
                                      }
                                    />
                                    <ErrorMessage
                                      name="ticketCreditPreference"
                                      component="span"
                                      className={classes.error}
                                    />
                                  </>
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={classes.dividerContainer}>
                      <hr className={classes.border} />
                    </div>

                    <div className={classes.formCont}>
                      <div className="row">
                        <div className="col-12 col-md-4">
                          <div className={classes.location}>
                            <FormattedMessage id="admin.editCompany.guestProfile" />
                          </div>
                          <div className={classes.description}>
                            <FormattedMessage id="admin.editCompany.guestProfileDescription" />
                          </div>
                        </div>
                        <div className="col-12 col-md-8">
                          <div className={classes.formContainer}>
                            <div className={classes.inputContainer}>
                              <label htmlFor="guestProfileVisibility" className={classes.preferenceLabel}>
                                <FormattedMessage id="admin.editCompany.guestProfileLabel" />
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip
                                      className={`${general.main} ${classes.tooltip}`}
                                    >
                                      <div className={classes.infoDescription}>
                                        <FormattedMessage
                                          id="admin.editCompany.guestProfileTooltip"
                                        />
                                      </div>
                                    </Tooltip>
                                  }
                                >
                                  <InfoIconSvg className={classes.infoIcon} />
                                </OverlayTrigger>
                              </label>
                              <Field
                                type="select"
                                name="guestProfileVisibility"
                                id="guestProfileVisibility"
                                render={({ form }) => (
                                  <>
                                    <UCSelect
                                      isSearchable={false}
                                      fieldHasError={
                                        errors.guestProfileVisibility &&
                                        touched.guestProfileVisibility
                                      }
                                      displayName={
                                        <FormattedMessage id="admin.editCompany.guestProfileLabel" />
                                      }
                                      value={values.guestProfileVisibility}
                                      options={guestProfileVisibilityOptions}
                                      defaultValue={guestProfileVisibilityOptions.find(item => item.value === values.guestProfileVisibility)}
                                      onChange={(item) =>
                                        form.setFieldValue(
                                          "guestProfileVisibility",
                                          item.value
                                        )
                                      }
                                    />
                                    <ErrorMessage
                                      name="guestProfileVisibility"
                                      component="span"
                                      className={classes.error}
                                    />
                                  </>
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={classes.dividerContainer}>
                      <hr className={classes.border} />
                    </div>

                    <div className={classes.formCont}>
                      <div className="row">
                        <div className="col-12 col-md-4">
                          <div className={classes.location}>
                            <FormattedMessage id="settings.membership" />
                          </div>
                          <div className={classes.description}>
                            <FormattedMessage id="settings.membershipDescription" />
                          </div>
                        </div>
                        <div className="col-12 col-md-8">
                          <div className={classes.formContainer}>
                            <div className={classes.inputContainer}>
                              <label htmlFor="businessAccountNumber" className={classes.label}>
                                {<FormattedMessage id="admin.editCompany.mileagePlanNumber" />}
                              </label>
                              <Field
                                placeholder={intl.formatMessage(messages.mileagePlanNumberPlaceholderMessage)}
                                type="text"
                                name="businessAccountNumber"
                                id="businessAccountNumber"
                                disabled={!checkPermission("F_MANAGE_COMPANY_MEMBERSHIP_NUMBER") || companyDetails?.companyStatus === "REMOVED"}
                              />
                              {companyDetails?.showDiscountTerms && (
                                <UCButton
                                  type="defaultLink"
                                  className={classes.contractDetailsLink}
                                  onClick={() => setShowModal(true)}
                                  text={<FormattedMessage id="fareAccess.toggle.contractFareDetailsModalLink" />}
                                />
                              )}
                            </div>

                            <div className={classnames(classes.inputContainer, classes.inputContainerMultipleFields)}>
                              <label htmlFor="haCorporateProgramEnrolled" className={classes.label}>
                                <FormattedMessage id="admin.editCompany.haCorporateProgram" />
                              </label>
                              <Field
                                placeholder={intl.formatMessage(messages.haCorporateProgramEnrolledPlaceholderMessage,)}
                                type="select"
                                name="haCorporateProgramEnrolled"
                                id="haCorporateProgramEnrolled"
                                render={({ form }) => (
                                  <div>
                                    <UCSelect
                                      isSearchable={false}
                                      styles={hawaianSelect}
                                      displayName={
                                        <FormattedMessage
                                          id="admin.editCompany.haCorporateProgramEnrolledPlaceholder" />
                                      }
                                      value={values.haCorporateProgramEnrolled}
                                      options={[{
                                        value: true,
                                        label: intl.formatMessage(messages.haCorporateProgramEnrolledYesMessage),
                                      }, {
                                        value: false,
                                        label: intl.formatMessage(messages.haCorporateProgramEnrolledNoMessage),
                                      }]}
                                      onChange={(item) => {
                                        form.setFieldValue('haCorporateProgramEnrolled', item.value);
                                        form.setFieldValue('hawaiianNumber', "")
                                      }}
                                      disabled={hawaiianNumberDisabled}
                                    />
                                    <ErrorMessage
                                      name="haCorporateProgramEnrolled"
                                      component="span"
                                      className={classes.error}
                                    />
                                  </div>
                                )}
                              />

                              {values.haCorporateProgramEnrolled && <>
                                <Field
                                  placeholder={intl.formatMessage(messages.hawaiianNumberPlaceholderMessage,)}
                                  type="text"
                                  maxLength={9}
                                  name="hawaiianNumber"
                                  id="hawaiianNumber"
                                  disabled={hawaiianNumberDisabled}
                                  className={
                                    errors.hawaiianNumber && touched.hawaiianNumber
                                      ? 'input-error'
                                      : null
                                  }
                                />
                                <ErrorMessage
                                  name="hawaiianNumber"
                                  component="span"
                                  className={classes.error}
                                />
                              </>}
                            </div>

                            {
                              checkPermission("F_MANAGE_COMPANY_STAR") && providerCode === AIRLINES.AS &&
                              <>
                                {userDetails?.manageCompany && (
                                  <>
                                    <div className={classes.inputContainer}>
                                      <label htmlFor="companyStar" className={classes.label}>
                                        <FormattedMessage id="admin.editCompany.companyStar" />
                                      </label>
                                      <Field
                                        placeholder={intl.formatMessage(messages.companyStarPlaceholder)}
                                        type="text"
                                        name="companyStar"
                                        id="companyStar"
                                        className={
                                          errors.companyStar && touched.companyStar
                                            ? "input-error"
                                            : null
                                        }
                                      />
                                      <ErrorMessage
                                        name="companyStar"
                                        component="span"
                                        className={classes.error}
                                      />
                                    </div>
                                  </>
                                )}
                              </>
                            }
                            {checkPermission("F_COMPANYSETTINGS_REFERRALCODE_VIEW") && providerCode === AIRLINES.AS && (
                              <>
                                <div className={`${classes.inputContainer} ${classes.refferalInputContainer}`}>
                                  <label htmlFor="referralCode" className={classes.label}>
                                    <FormattedMessage id="settings.referralCode" />
                                  </label>
                                  <Field
                                    placeholder={intl.formatMessage(messages.referralCodePlaceholderMessage)}
                                    type="text"
                                    name="referralCode"
                                    id="referralCode"
                                    disabled={!checkPermission("F_COMPANYSETTINGS_REFERRALCODE_EDIT")}
                                    className={
                                      errors.referralCode && touched.referralCode
                                        ? "input-error"
                                        : null
                                    }
                                  />
                                  <ErrorMessage
                                    name="referralCode"
                                    component="span"
                                    className={classes.error}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={classes.dividerContainer}>
                    <hr className={classes.border} />
                  </div>

                  {checkPermission("F_COMPANY_DELETE") && companyDetails?.companyStatus && (
                    <div>
                      <div className={classes.formCont}>
                        <div className="row">
                          <div className="col-12 col-md-4">
                            <div className={classes.location}>
                              <FormattedMessage id="settings.deleteBusiness" />
                            </div>
                            <div className={classes.description}>
                              <FormattedMessage id="settings.deleteBusinessDescription" />
                            </div>
                          </div>

                          {companyDetails?.companyStatus === "ACTIVE" &&
                            <div className="col-12 col-md-8">
                              <UCButton
                                type="danger"
                                className={classes.deleteCompanyButton}
                                text={<FormattedMessage id="settings.deleteCompanyButton" />}
                                onClick={() => setShowDeleteModal(true)}
                              />
                            </div>
                          }

                          {companyDetails?.companyStatus === "PENDING_REMOVAL" &&
                            <div className="col-12 col-md-8">
                              <div className="row">
                                <div className={`col-12 col-md-auto ${classes.warningIconContainer}`}><WarningCompanyDeleteIcon /></div>
                                <div className="col-12 col-md">
                                  <p className={classes.accountClosedTitle}><FormattedMessage id="settings.accountClosedTitle" /></p>
                                  <p className={classes.accountClosedDescription}><FormattedMessage id="settings.accountClosedPendingDescription" values={{
                                    atmName: <span className={classes.accountClosedCustomValues}>{companyDetails?.removalRequestedByFirstName} {companyDetails?.removalRequestedByLastName}</span>,
                                    removalDate: <span className={classes.accountClosedCustomValues}>{companyDetails?.removalDate}</span>
                                  }} /></p>
                                </div>
                              </div>
                            </div>
                          }

                        </div>
                      </div>
                      <div className={classes.dividerContainer}>
                        <hr className={classes.border} />
                      </div>
                    </div>
                  )}

                  <Row>
                    <Col>
                      <div className={classes.buttonContainer}>
                        <div className={classes.btnContainer}>
                          <UCButton
                            type="default"
                            className={classes.buttonCancel}
                            onClick={cancelUpdate}
                            text={<FormattedMessage id="settings.cancel" />}
                          />
                          <UCButton
                            type="primary"
                            className={classes.button}
                            onClick={handleFormSubmit}
                            text={<FormattedMessage id="settings.save" />}
                            spinner={{
                              name: "updateCompanySpinner",
                              size: 20,
                              color: colors.white
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </form>
              )}
            </Formik>
          }

          {
            showDeleteModal &&
              <DeleteCompanyMasterModal
                showModal={showDeleteModal}
                setShowModal={setShowDeleteModal}
                modalIcon={<DeleteCompanyIcon />}
                setSelectedRow={(value) => console.log(value)}
              />
          }

          {companyDetails?.mustChangeEmailAddress &&
            <UpdateCitiEmailModal
              showUpdateCitiEmailModal={showUpdateCitiEmailModal}
              setShowUpdateCitiEmailModal={setShowUpdateCitiEmailModal}
              companyDetails={companyDetails}
            />
          }
          <UCModal
            className={classes.contractFareDetailsModal}
            showModal={showModal}
            setShowModal={setShowModal}
            modalTitle={discountTermsContent?.CampaignName}
            modalContent={
              isLoadingDiscountTerms ? (
                <ASTextLoadingSpinner loadingText={true} />
              ) : !discountTermsContent?.CampaignRestrictionMemo ? (
                <div className={classes.errorContentWrapper}>
                  <p className={classes.errorTitle}><FormattedMessage id="fareAccess.contractFareDetails.error.title" /></p>
                  <p className={classes.errorText}>
                    <FormattedMessage id="fareAccess.contractFareDetails.error.description" />
                  </p>
                  <UCButton
                    type="default"
                    className={classes.closeButton}
                    onClick={() => setShowModal(false)}
                    text={<FormattedMessage id="fareAccess.contractFareDetails.error.closeButtonText" />}
                    spinner={{
                      name: "updateFareAccessSpinner",
                      size: 20,
                      color: colors.white,
                    }}
                  />
                </div>
              ) : discountTermsContent?.CampaignRestrictionMemo ? (
              <div
                  className={classes.modalContentWrapper}
                  dangerouslySetInnerHTML={{
                    __html: discountTermsContent.CampaignRestrictionMemo
                  }}
                />
              ) : null
            }
            dataTestId="contractFareDetailsModal"
          />
        </div>
      ) : (
        <GeneralTabSkeleton />
      )}
    </>
  )
}

export default GeneralTabAS;
